/**
 * Mastermind strike handler dispatcher for the ImplementationMap.
 *
 * The exported `mastermindStrikeHandler` is a dispatcher that branches on
 * `G.selection.mastermindId`. Per-mastermind handlers implement card-text
 * effects (e.g., Magneto forces each player to discard down to four cards).
 * The generic strike-counter increment runs for every mastermind so
 * card-specific handlers stay focused on the card text. A Master Strike
 * resolves only its printed text: it captures no Bystander unless that
 * text says so, and no printed capture is modeled yet (D-24654, which
 * supersedes D-15401).
 *
 * No boardgame.io imports. No registry imports.
 */
import { reshuffleDiscardIntoDeck } from '../moves/drawCards.logic.js';
import { composeMastermindStrikeNarrative, composeStrikeBlockedNarrative, } from '../events/notableEvents.compose.js';
import { pushLog } from '../log/logPush.js';
import { moveCardFromZone } from '../moves/zoneOps.js';
import { discardFromHand } from '../moves/discardFromHand.js';
import { koCard } from '../board/ko.logic.js';
import { recordOwnedCardKoed } from '../hero/deferredConditionalGrants.js';
import { WOUND_EXT_ID } from '../setup/pilesInit.js';
import { gainWoundWithOutcome } from '../board/wounds.logic.js';
import { seatsInTurnOrderFrom } from '../turn/seatOrder.logic.js';
import { formatCardRef, resolveCardName } from '../log/logDisplay.js';
// why: WP-669 / D-24483 — the Mastermind Transform flip primitive; resolveGeneralRossStrike
// swaps the boss face on a Master Strike.
import { transformMastermind } from '../mastermind/mastermind.logic.js';
// why: WP-577 / D-24386 — Red Skull's strike parks a hand-scoped interactive
// ko-hero choice for the current player, reusing the same eligibility helpers the
// villain ko-hero effect + the UIState projection use (one source, D-24007).
import { buildKoEligibleTargets, countKoableHeroes } from '../villain/villainEffects.execute.js';
import { matchesGreyHeroFilter, offPlayCardStats, offPlayCardTraits } from '../hero/splitCard.logic.js';
// why: WP-818 / D-24752 — Baron Zemo's strike KOs the first Victory-Pile Bystander through
// the shared WP-816 helper (defeated Killbots included), and reads the Zemo id from its one
// home (WP-819 / D-24753).
import { findFirstVictoryPileBystander } from '../board/bystanders.logic.js';
import { BARON_ZEMO_MASTERMIND_ID } from '../economy/rescueOnFight.logic.js';
// why: WP-821 / D-24754 — Arnim Zola's strike reads the one "less than 2 printed Attack" HQ
// predicate, puts each such Hero on the Hero Deck bottom and refills its slot (the
// resolveOptionalPutBottomHQ idiom), and auto-picks each player's discard through the
// WP-817 cost-filtered default pick.
import { ARNIM_ZOLA_MASTERMIND_ID, listZolaWeakHqSlots } from '../mastermind/arnimZola.logic.js';
import { refillHqSlot } from '../board/city.logic.js';
import { selectDefaultCostDiscardCard } from '../moves/villainDefeatRequirement.logic.js';
// why: mastermind ext_id constants — matching against
// G.selection.mastermindId for per-mastermind dispatch. Strings come from
// the registry; the runtime loadout
// (apps/arena-client/public/loadout-test.json) and tests use
// `core/<slug>`.
const MASTERMIND_MAGNETO = 'core/magneto';
// why: core Magneto's Master Strike text: "Each player reveals an
// [team:x-men] Hero or discards down to four cards." WP-476 / D-24284
// implements the printed conditional in full: a player holding an X-Men Hero
// reveals it and discards nothing; a player who must discard does so — the
// CURRENT player via an interactive pending-choice (they pick which cards),
// non-current players via a deterministic cheapest-first auto-pick. Team
// affiliation is on G.cardTraits (WP-179). Note this is core/magneto only —
// co2e/magneto prints DIFFERENT text and has its own resolver.
const MAGNETO_HAND_SIZE_LIMIT = 4;
// why: both Red Skull faces print the identical Master Strike text —
// "Each player KOs a Hero from their hand." The co2e 2nd-edition base face
// (`co2e/red-skull`) matches core's text verbatim, and mastermind setup
// selects the first non-tactic face, so a co2e Red Skull resolves to this
// base face. The co2e epic face (`epic-red-skull`) prints different text
// and is never engine-selectable, so it is deliberately excluded here.
const MASTERMINDS_RED_SKULL = [
    'core/red-skull',
    'co2e/red-skull',
];
// why: core Loki's Master Strike — "Each player reveals a [hc:strength] Hero
// or gains a Wound." This is core/loki ONLY; co2e/loki prints a DIFFERENT
// penalty (discard a Strength Hero / stack a Hypno-Thrall) and has its own
// resolver (resolveLokiStrike), so folding them would play the wrong text.
const MASTERMIND_CORE_LOKI = 'core/loki';
// why: WP-388 / D-24192 — the four remaining co2e masterminds whose printed
// Master Strike is implemented. Each is its own constant because each prints
// different text; there is no shared-text list like Red Skull's.
//
// why: co2e/magneto is deliberately SEPARATE from MASTERMIND_MAGNETO above.
// The two Magnetos print different strikes (core: reveal-or-discard-to-four;
// co2e: discard an X-Men Hero or gain a Wound), so folding co2e into the core
// branch would play the wrong card text.
//
// why: base faces only. Each co2e mastermind also ships an epic face printing
// different text, which is not engine-selectable — mastermind setup takes the
// FIRST non-tactic face (D-24193 / WP-389), and no epic opt-in exists yet.
const MASTERMIND_CO2E_DOCTOR_DOOM = 'co2e/doctor-doom';
const MASTERMIND_CO2E_LOKI = 'co2e/loki';
const MASTERMIND_CO2E_MAGNETO = 'co2e/magneto';
const MASTERMIND_CO2E_DOCTOR_OCTOPUS = 'co2e/doctor-octopus';
// why: co2e Loki and Doctor Octopus gate on these normalized lowercase trait
// slugs, matching G.cardTraits values verbatim (traits.normalize.ts lowercases
// and trims only — it does not rewrite separators).
const TEAM_X_MEN = 'x-men';
const TEAM_SPIDER_FRIENDS = 'spider-friends';
const HERO_CLASS_STRENGTH = 'strength';
const HERO_CLASS_TECH = 'tech';
// why: core Dr. Doom's Master Strike — "Each player with exactly 6 cards in hand
// reveals a [hc:tech] Hero or puts 2 cards from their hand on top of their deck."
// This is core/dr-doom ONLY; co2e/doctor-doom prints DIFFERENT text and has its
// own resolver (resolveDoctorDoomStrike), so folding them would play the wrong
// card text.
const MASTERMIND_CORE_DR_DOOM = 'core/dr-doom';
// why: core Dr. Doom's Strike affects only players holding EXACTLY this many
// cards; a player with any other hand size is unaffected.
const DOOM_STRIKE_HAND_GATE = 6;
// why: WP-669 / D-24483 — General "Thunderbolt" Ross is the first transforming
// mastermind. Its Master Strike prints "General Ross [keyword:Transforms], then
// [Cross-Dimensional Hulk Rampage]" and Red Hulk's flips back — resolveGeneralRossStrike
// flips the boss face. The named ride-along effect is honest-partial (logged) this slice.
const MASTERMIND_GENERAL_ROSS = 'wwhk/general-thunderbolt-ross';
// why: the printed penalty puts exactly this many cards on top of the deck.
const DOOM_PUT_ON_DECK_COUNT = 2;
// why: WP-397 — Doctor Octopus's printed strike reveals the top 8 cards of the
// deck for any player who does not discard a Spider-Friends Hero.
const DOCTOR_OCTOPUS_REVEAL_COUNT = 8;
/**
 * Narrows the rule-pipeline context to its deterministic shuffle function.
 *
 * @param context - The context the rule pipeline passed to the handler.
 * @returns The Shuffle function, or null when the context does not carry one.
 */
function resolveShuffleFunction(context) {
    // why: `performVillainReveal` passes the full RevealContext into
    // executeRuleHooks precisely so handlers can reach `context.random.Shuffle`
    // (see the Step-5 `// why:` there) — the strike handler previously ignored
    // it. Narrowed structurally rather than by importing RevealContext, which
    // would couple the rules module to the villain-deck module, and without any
    // boardgame.io type.
    if (typeof context !== 'object' || context === null) {
        return null;
    }
    const random = context.random;
    if (typeof random !== 'object' || random === null) {
        return null;
    }
    const shuffle = random.Shuffle;
    if (typeof shuffle !== 'function') {
        return null;
    }
    return shuffle;
}
/**
 * Builds the shared counter-increment effects that fire for every
 * mastermind strike regardless of the card-specific text.
 *
 * @returns Generic RuleEffect[] applied to every strike.
 */
function buildGenericStrikeEffects() {
    return [
        {
            type: 'modifyCounter',
            counter: 'masterStrikeCount',
            delta: 1,
        },
        {
            type: 'queueMessage',
            message: 'Mastermind strike revealed — strike count incremented.',
        },
    ];
}
/**
 * Whether a player holds an X-Men Hero in HAND (the Magneto reveal branch).
 *
 * @param gameState - The game state (read-only; supplies cardTraits).
 * @param hand - The player's hand, in order.
 * @returns True when the hand holds at least one Hero whose team is 'x-men'.
 */
function playerHasXMenHeroInHand(gameState, hand) {
    // why: HAND-ONLY. Magneto's strike fires at start-of-turn (villainDeck.reveal
    // gated on currentStage==='start'), so every player's hand is full and their
    // inPlay zone is empty — the reveal has nothing to check in play. Do NOT copy
    // D-24281's hand+in-play scope; that ruling is for the reveal-or-wound handler,
    // whose strike timing differs. Reuses selectLowestCostHero's existing team
    // scan (non-null iff a matching Hero exists), so no second cardTraits reader.
    return selectLowestCostHero(gameState, hand, 'team', TEAM_X_MEN) !== null;
}
/**
 * Narrows the rule-pipeline context to the active player id.
 *
 * @param context - The context the rule pipeline passed to the handler.
 * @returns The current player id, or null when the context does not carry one.
 */
function resolveCurrentPlayer(context) {
    // why: the strike handler receives the villain-reveal RevealContext (the same
    // object resolveShuffleFunction narrows for Doctor Octopus). It carries
    // `ctx.currentPlayer` — the active player who parks the interactive discard.
    // Narrowed structurally (no RevealContext import) so the rules module stays
    // decoupled from the villain-deck module and free of any boardgame.io type. A
    // context without it (unit-test stubs passing `{}`) yields null → the strike
    // auto-picks every discard deterministically instead of parking a choice.
    if (typeof context !== 'object' || context === null) {
        return null;
    }
    const ctx = context.ctx;
    if (typeof ctx !== 'object' || ctx === null) {
        return null;
    }
    const currentPlayer = ctx.currentPlayer;
    if (typeof currentPlayer !== 'string' || currentPlayer.length === 0) {
        return null;
    }
    return currentPlayer;
}
/**
 * Selects the cheapest cards to discard down to a hand-size limit, keeping the
 * most expensive cards. Deterministic — cheapest cost first, ties broken by
 * lowest hand index.
 *
 * Used by both the Magneto strike's non-current-player auto-pick and the bot/sim
 * resolution of the current player's parked choice, so the two stay identical.
 *
 * @param gameState - The game state (read-only; supplies cardStats).
 * @param hand - The player's hand, in order.
 * @param discardCount - How many cards to select for discard.
 * @returns The chosen ext_ids (length min(discardCount, hand.length)).
 */
export function selectDiscardToLimitCards(gameState, hand, discardCount) {
    // why: work on hand INDICES, not ext_ids, so duplicate ext_ids and Wounds are
    // each selectable exactly once — selecting by ext_id could double-count a
    // repeated card or never reach the count. remainingIndices starts in ascending
    // hand-index order so a cost tie keeps the lowest index (matches
    // selectLowestCostHero's strict-`<` tiebreak).
    const remainingIndices = [];
    for (let handIndex = 0; handIndex < hand.length; handIndex++) {
        remainingIndices.push(handIndex);
    }
    const selected = [];
    while (selected.length < discardCount && remainingIndices.length > 0) {
        let cheapestSlot = 0;
        let cheapestCost = Number.POSITIVE_INFINITY;
        for (let slot = 0; slot < remainingIndices.length; slot++) {
            const handIndex = remainingIndices[slot];
            const cardExtId = hand[handIndex];
            // why: unlike selectLowestCostHero (which skips Wounds because ITS effects
            // target Heroes), "discard down to four cards" counts EVERY card in hand,
            // Wounds included — and a Wound is the least valuable card to keep, so at
            // cost 0 it sorts cheapest and is discarded first. S.H.I.E.L.D. starters
            // likewise carry no cardStats entry (D-21502) → cost 0.
            const cardCost = gameState.cardStats[cardExtId]?.cost ?? 0;
            // why: strict `<` keeps the FIRST (lowest hand index) card on a cost tie.
            if (cardCost < cheapestCost) {
                cheapestCost = cardCost;
                cheapestSlot = slot;
            }
        }
        const chosenHandIndex = remainingIndices[cheapestSlot];
        selected.push(hand[chosenHandIndex]);
        remainingIndices.splice(cheapestSlot, 1);
    }
    return selected;
}
/**
 * Resolves Magneto's Master Strike: "Each player reveals an [team:x-men] Hero
 * or discards down to four cards." (WP-476 / D-24284.)
 *
 * Each player (current player first, then clockwise; D-24780): a player holding an X-Men Hero in hand
 * reveals it and discards nothing; otherwise a player over the limit discards
 * down to four. The CURRENT player discards INTERACTIVELY — a PendingDiscardChoice
 * is parked and they pick which cards (the printed player choice restored).
 * Non-current players auto-pick the cheapest cards deterministically, because
 * the pending-choice architecture is single-current-player-scoped (see
 * WP-476 §Scope Out). For 1p (the reported case) the current player is the only
 * player, so this fully fixes the bug.
 *
 * Mutates G directly. Per-player messages are appended to G.messages so replay
 * tooling can observe the effect.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player id (parks the interactive choice), or
 *   null when the context carried no current player (all players auto-pick).
 */
function resolveMagnetoStrike(gameState, currentPlayer) {
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        const startingHandSize = playerZones.hand.length;
        // why: the reveal branch of the printed "reveals an [team:x-men] Hero OR
        // discards" — a player holding an X-Men Hero reveals it and is skipped.
        if (playerHasXMenHeroInHand(gameState, playerZones.hand)) {
            pushLog(gameState, `[Magneto Master Strike] Player ${playerId} revealed an X-Men Hero — no discard.`);
            // why: WP-644 / D-24456 — announce the avoided Master Strike, additive to
            // the silent reveal-skip (the deckReshuffled push idiom). One event per
            // blocking player; the terminal mastermindStrikeResolved still fires below.
            gameState.notableEvents.push({
                type: 'strikeBlocked',
                playerId,
                threatKind: 'masterStrike',
                narrative: composeStrikeBlockedNarrative('masterStrike'),
            });
            continue;
        }
        if (startingHandSize <= MAGNETO_HAND_SIZE_LIMIT) {
            pushLog(gameState, `[Magneto Master Strike] Player ${playerId} already has ${startingHandSize} card(s) in hand — no discard.`);
            continue;
        }
        const discardCount = startingHandSize - MAGNETO_HAND_SIZE_LIMIT;
        // why: the CURRENT player discards INTERACTIVELY — park a PendingDiscardChoice
        // (KO nothing yet) and let them choose which cards to discard. Non-current
        // players auto-pick below because the pending-choice architecture is
        // single-current-player-scoped (D-24284): the block-all guards, UIState
        // projection, and bot resolver all key on the active player. Lazy-init the
        // queue at the park site (never in Game.setup — undefined = no pending
        // choice). currentPlayer is null only for legacy test harnesses passing no
        // ctx; they fall through to the deterministic auto-pick so the strike never
        // blocks on an unknown chooser.
        if (currentPlayer !== null && playerId === currentPlayer) {
            if (!gameState.pendingDiscardChoices) {
                gameState.pendingDiscardChoices = [];
            }
            gameState.pendingDiscardChoices.push({
                choiceType: 'discard-to-limit',
                playerID: playerId,
                limit: MAGNETO_HAND_SIZE_LIMIT,
            });
            pushLog(gameState, `[Magneto Master Strike] Player ${playerId} must discard down to ${MAGNETO_HAND_SIZE_LIMIT} — choose which cards to discard.`);
            continue;
        }
        // why: non-current player — auto-pick the cheapest cards to discard down to
        // the limit (keeping the expensive Heroes; Wounds sort cheapest and go
        // first), deterministically. NOT Magneto's former front-of-hand
        // hand.slice(0, discardCount), which discarded arbitrary cards.
        const cardsToDiscard = selectDiscardToLimitCards(gameState, playerZones.hand, discardCount);
        for (const cardExtId of cardsToDiscard) {
            discardCardFromHand(gameState, playerId, cardExtId);
        }
        pushLog(gameState, `[Magneto Master Strike] Player ${playerId} discarded ${cardsToDiscard.length} card(s) down to ${MAGNETO_HAND_SIZE_LIMIT}.`);
    }
}
/**
 * Resolves core Dr. Doom's Master Strike: "Each player with exactly 6 cards in
 * hand reveals a [hc:tech] Hero or puts 2 cards from their hand on top of their
 * deck."
 *
 * Per player (sorted id order): a player not holding exactly DOOM_STRIKE_HAND_GATE
 * cards is unaffected. A gated player holding a Tech Hero reveals it and KEEPS it
 * (the printed escape, no penalty). Otherwise the player puts
 * DOOM_PUT_ON_DECK_COUNT cards on top of their deck — the CURRENT player
 * INTERACTIVELY (park a PendingPutCardsOnDeckChoice; they pick which cards and the
 * top order), non-current players via a deterministic cheapest-first auto-pick
 * (mirroring resolveMagnetoStrike, D-24347).
 *
 * Mutates G directly. One durable log line per player.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in legacy test harnesses),
 *   narrowed from the strike RevealContext; only they park interactively.
 */
function resolveCoreDoomStrike(gameState, currentPlayer) {
    const playerIds = Object.keys(gameState.playerZones).sort();
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        // why: the exactly-6-cards gate — a player with any other hand size is
        // untouched by the printed strike.
        if (playerZones.hand.length !== DOOM_STRIKE_HAND_GATE) {
            pushLog(gameState, `[Dr. Doom Master Strike] Player ${playerId} does not have exactly ${DOOM_STRIKE_HAND_GATE} cards in hand — unaffected.`);
            continue;
        }
        // why: the reveal branch of the printed "reveals a [hc:tech] Hero OR puts 2
        // cards on top" — a gated player holding a Tech Hero reveals it and KEEPS it,
        // taking no penalty. selectLowestCostHero is the existence check only; the
        // revealed card is never removed.
        if (selectLowestCostHero(gameState, playerZones.hand, 'heroClass', HERO_CLASS_TECH) !== null) {
            pushLog(gameState, `[Dr. Doom Master Strike] Player ${playerId} revealed a Tech Hero — no cards put on deck.`);
            // why: WP-645 / D-24457 — announce the avoided Dr. Doom strike (the third
            // reveal-to-avoid Master Strike producer, deferred by WP-644), additive to
            // the silent reveal-skip. One event per blocking player; the terminal
            // mastermindStrikeResolved still fires below. The WP-644 Magneto push idiom.
            gameState.notableEvents.push({
                type: 'strikeBlocked',
                playerId,
                threatKind: 'masterStrike',
                narrative: composeStrikeBlockedNarrative('masterStrike'),
            });
            continue;
        }
        // why: the CURRENT player puts cards on deck INTERACTIVELY — park a
        // PendingPutCardsOnDeckChoice and let them choose which cards and the top
        // order. Non-current players auto-pick below because the pending-choice
        // architecture is single-current-player-scoped (D-24347): the block-all
        // guards, UIState projection, and bot resolver all key on the active player.
        // Lazy-init the queue at the park site (never in Game.setup — undefined = no
        // pending choice). currentPlayer is null only for legacy test harnesses
        // passing no ctx; they fall through to the deterministic auto-pick so the
        // strike never blocks on an unknown chooser.
        if (currentPlayer !== null && playerId === currentPlayer) {
            if (!gameState.pendingPutCardsOnDeckChoices) {
                gameState.pendingPutCardsOnDeckChoices = [];
            }
            gameState.pendingPutCardsOnDeckChoices.push({
                choiceType: 'put-cards-on-deck',
                playerID: playerId,
                count: DOOM_PUT_ON_DECK_COUNT,
            });
            pushLog(gameState, `[Dr. Doom Master Strike] Player ${playerId} must put ${DOOM_PUT_ON_DECK_COUNT} cards on top of their deck — choose which cards.`);
            continue;
        }
        // why: non-current player — auto-pick the cheapest cards to put on top
        // (keeping the expensive Heroes; Wounds sort cheapest and go first),
        // deterministically. selectDiscardToLimitCards is the SAME cheapest-first
        // selector the current player parks against, so a bot's placement is identical
        // whether it parked or auto-picked.
        const cardsToPut = selectDiscardToLimitCards(gameState, playerZones.hand, DOOM_PUT_ON_DECK_COUNT);
        let workingHand = playerZones.hand;
        const movedCards = [];
        for (const cardExtId of cardsToPut) {
            const moveResult = moveCardFromZone(workingHand, [], cardExtId);
            if (!moveResult.found) {
                continue;
            }
            workingHand = moveResult.from;
            movedCards.push(cardExtId);
        }
        playerZones.hand = workingHand;
        playerZones.deck = [...movedCards, ...playerZones.deck];
        pushLog(gameState, `[Dr. Doom Master Strike] Player ${playerId} put ${movedCards.length} card(s) on top of their deck.`);
    }
}
// why: WP-577 / D-24386 — Red Skull's Master Strike is "KO a Hero from their
// hand," so the interactive choice, the resolve move, and the bot default are all
// hand-scoped. A module const keeps the one zone allowlist in a single place.
const RED_SKULL_KO_ZONES = ['hand'];
/**
 * Selects the Hero a player KOs for a Red Skull Master Strike: the eligible
 * hand card with the lowest recruit cost, ties broken by lowest hand index.
 * A hand card is a Hero iff it is not a Wound.
 *
 * Exported (WP-577) so the bot's resolution of the current-player interactive
 * choice is byte-identical to this auto-pick — the ally auto-KO and the bot
 * current-player KO share one selection rule.
 *
 * @param gameState - The game state (read-only here; supplies cardStats).
 * @param hand - The player's hand, in order.
 * @returns The ext_id to KO, or null when the hand holds no Hero.
 */
export function selectRedSkullKoTarget(gameState, hand) {
    // why: WP-388 — Red Skull's rule is the ungated case of the shared
    // selector. Delegating keeps its behavior byte-identical while giving the
    // co2e resolvers one selection rule to share.
    return selectLowestCostHero(gameState, hand, 'any', null);
}
/**
 * Selects the lowest-cost Hero in a hand, optionally gated on one trait.
 *
 * Shared by every per-mastermind strike resolver. Ties are broken by lowest
 * hand index. Returns null when the hand holds no matching Hero.
 *
 * @param gameState - The game state (read-only here; supplies cardStats and cardTraits).
 * @param hand - The player's hand, in order.
 * @param traitKind - Which trait to gate on, or `'any'` for no gate.
 * @param traitSlug - The normalized lowercase slug to match; ignored when traitKind is `'any'`.
 * @returns The selected ext_id, or null when nothing matches.
 */
function selectLowestCostHero(gameState, hand, traitKind, traitSlug) {
    let selectedExtId = null;
    let selectedCost = Number.POSITIVE_INFINITY;
    for (const cardExtId of hand) {
        // why: Wounds are not Heroes — the only non-Hero card the engine ever
        // puts in a hand — so they are never a selection target.
        if (cardExtId === WOUND_EXT_ID) {
            continue;
        }
        // why: a plain discriminator argument rather than a predicate callback —
        // `.claude/rules/code-style.md` §Functions bans closures-as-config. Trait
        // values come from G.cardTraits (WP-179), resolved at setup so no registry
        // read is needed at runtime; a card with no entry yields undefined and
        // simply never matches.
        if (traitKind !== 'any') {
            // why: defensive `?.` on the map itself, matching the cardDisplayData
            // precedent in this file — legacy test states predate G.cardTraits
            // (WP-179) and leave it undefined. Production setup always builds it.
            // A missing map means nothing matches rather than a throw (AC-9).
            if (traitKind === 'non-grey') {
                // why: WP-398 — an EXISTENCE gate, not a slug match (traitSlug is ignored), used by
                // co2e Loki's Hypno-Thrall branch to stack the lowest-cost non-grey Hero. D-24783 —
                // the shared grey predicate decides it: a card with a class on EITHER off-play slot
                // (a dual-class card, or a split card through the split-face map) is non-grey.
                if (!matchesGreyHeroFilter(gameState, cardExtId, 'non-grey')) {
                    continue;
                }
            }
            else {
                // why: WP-772 / D-24604 — every caller passes a HAND (off play), so a split card counts
                // as both halves' classes (rules v23 p.49), resolved through the split-face map.
                const traitEntry = offPlayCardTraits(gameState, cardExtId);
                // why: 'team' / 'heroClass' match the corresponding G.cardTraits field
                // against the requested slug.
                // why: WP-703 / D-24523 — on a heroClass kind a dual-class card matches on EITHER printed class.
                const matchesTrait = traitKind === 'team'
                    ? traitEntry?.team === traitSlug
                    : (traitEntry?.heroClass === traitSlug || traitEntry?.heroClass2 === traitSlug);
                if (!matchesTrait) {
                    continue;
                }
            }
        }
        // why: `?? 0` — S.H.I.E.L.D. starters (Agent / Trooper) carry no
        // cardStats entry (D-21502) and are treated as cost 0, so a starter is
        // picked over any priced Hero (the cheapest-card tabletop instinct).
        const cardCost = gameState.cardStats[cardExtId]?.cost ?? 0;
        // why: strict `<` keeps the FIRST (lowest hand index) card on a cost tie.
        if (cardCost < selectedCost) {
            selectedCost = cardCost;
            selectedExtId = cardExtId;
        }
    }
    return selectedExtId;
}
/**
 * Moves one card from a player's hand to their discard pile via the shared
 * forced-discard chokepoint.
 *
 * // why: WP-498 / D-24301 — a mastermind strike is "a card effect [that] makes
 * you discard", so it routes through discardFromHand, which fires the
 * return-on-discard reaction (Cyclops Unending Energy force-discarded to a
 * strike offers to return to hand). Takes gameState + playerId (not raw zones)
 * because the reaction reads G.heroAbilityHooks and parks on G.
 *
 * @param gameState - The game state, mutated in place.
 * @param playerId - The player whose hand card is discarded.
 * @param cardExtId - The card to discard.
 */
function discardCardFromHand(gameState, playerId, cardExtId) {
    discardFromHand(gameState, playerId, cardExtId);
}
/**
 * Gives one Wound from the supply to a player's discard pile via the
 * gainWoundForPlayer chokepoint.
 *
 * // why: WP-682 / D-24499 — routes through the chokepoint so a Master-Strike Wound
 * to a Diving-Block holder (which is frequently a NON-active seat, since Master
 * Strikes wound every player) gets the reveal window via the WP-684 seat-choice wave.
 *
 * // why: D-24673 — returns the outcome, not a boolean, so the strike's log line can
 * say "would gain a Wound — Diving Block can prevent it" while a reveal is pending
 * (match P9oVujYd2B9: Loki's strike logged "gained a Wound" for a prevented Wound).
 *
 * @param gameState - The game state to mutate.
 * @param playerId - The player who gains the Wound (may be a non-active seat).
 * @returns 'no-wound' when the supply is empty, else 'gained' or 'diving-block-pending'.
 */
function gainWoundToDiscard(gameState, playerId) {
    return gainWoundWithOutcome(gameState, playerId);
}
/**
 * The "gained a Wound" tail of a Master Strike line, honest about a pending Diving
 * Block reveal (D-24673).
 *
 * @param outcome - The outcome of the Wound just dealt ('gained' or 'diving-block-pending').
 * @returns "gained a Wound" or "would gain a Wound — Diving Block can prevent it".
 */
function describeStrikeWound(outcome) {
    if (outcome === 'diving-block-pending') {
        return 'would gain a Wound — Diving Block can prevent it';
    }
    return 'gained a Wound';
}
/**
 * Resolves Red Skull's Master Strike: "Each player KOs a Hero from their
 * hand." Each player (in sorted id order) KOs one Hero — the lowest-cost
 * eligible card, tie broken by lowest hand index (the D-24188 deterministic
 * auto-pick: lowest-cost ≈ the player-optimal tabletop pick, which avoids a
 * blocking multi-player pending-choice) — from their hand to the global KO
 * pile. A player with no Hero in hand (empty, or all Wounds) takes no KO and
 * logs a no-op line.
 *
 * Mutates G directly, mirroring resolveMagnetoStrike. One durable log line
 * per player.
 *
 * @param gameState - The game state to mutate.
 */
function resolveRedSkullStrike(gameState, currentPlayer) {
    const playerIds = Object.keys(gameState.playerZones).sort();
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        // why: WP-577 / D-24386 — the printed "KO a Hero from their hand" is an
        // owning-player CHOICE, so the CURRENT player picks interactively. Park a
        // hand-scoped ko-hero choice when a genuine choice exists (≥ 2 physical hand
        // Heroes AND ≥ 2 distinct options — the same park condition as the villain
        // ko-hero effect, hand-scoped); exactly one eligible Hero is FORCED (auto-KO,
        // no prompt) and zero is a no-op, both handled by the auto-pick fall-through
        // below. Non-current allies always auto-pick (the pending-choice architecture
        // is single-current-player-scoped, D-24284). currentPlayer is null only for
        // legacy harnesses passing no ctx; they fall through to the auto-pick so the
        // strike never blocks on an unknown chooser. Supersedes the D-24188 auto-pick.
        if (currentPlayer !== null && playerId === currentPlayer) {
            const handEligible = buildKoEligibleTargets(playerZones, RED_SKULL_KO_ZONES);
            if (countKoableHeroes(playerZones, RED_SKULL_KO_ZONES) > 1 && handEligible.length >= 2) {
                if (!gameState.pendingKoHeroChoices) {
                    gameState.pendingKoHeroChoices = [];
                }
                gameState.pendingKoHeroChoices.push({
                    choiceType: 'ko-hero',
                    playerID: playerId,
                    zones: RED_SKULL_KO_ZONES,
                });
                pushLog(gameState, `[Red Skull Master Strike] Player ${playerId} must KO a Hero from their hand — choose which.`);
                continue;
            }
            // why: exactly one eligible Hero (forced) or none — fall through to the
            // deterministic auto-pick, so a single-option pick is never prompted.
        }
        const targetExtId = selectRedSkullKoTarget(gameState, playerZones.hand);
        if (targetExtId === null) {
            pushLog(gameState, `[Red Skull Master Strike] Player ${playerId} has no Hero in hand to KO.`);
            continue;
        }
        // why: remove from hand then append to the global KO pile — the WP-382 /
        // D-24183 KO idiom. Duplicate ext_ids are fungible tokens, so removing the
        // first occurrence is observationally identical to removing the exact
        // selected index.
        const moveResult = moveCardFromZone(playerZones.hand, [], targetExtId);
        playerZones.hand = moveResult.from;
        gameState.ko = koCard(gameState.ko, targetExtId);
        // why: WP-853 / D-24789 — each player loses a card they own; credit that player.
        recordOwnedCardKoed(gameState, playerId, targetExtId, 'hand');
        pushLog(gameState, `[Red Skull Master Strike] Player ${playerId} KO'd ${formatCardRef(gameState.cardDisplayData, targetExtId)} from their hand.`);
    }
}
/**
 * Resolves co2e Doctor Doom's Master Strike: "Stack this Strike next to
 * Doctor Doom as 'Omen of Doom.' Then each player discards cards equal to
 * the number of Omens or gains a Wound."
 *
 * Each player (current player first, then clockwise; D-24780) whose hand can cover the Omen count
 * discards that many cards, lowest-cost first; everyone else gains a Wound.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function resolveDoctorDoomStrike(gameState, currentPlayer) {
    // why: D-24192 — the Omen count is DERIVED, not stored. Every Doom strike
    // stacks exactly one Omen, so the Omen total is the strike count. The
    // generic `modifyCounter` effect is applied by the pipeline AFTER this
    // handler returns, so the counter still holds the pre-strike value here and
    // the strike being resolved is `+ 1`. `?? 0` because G.counters is not
    // seeded at setup. No Omen zone is created.
    const omenCount = (gameState.counters.masterStrikeCount ?? 0) + 1;
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        // why: the printed choice is "discard N or gain a Wound". A hand that
        // cannot cover N takes the Wound — the same branch a tabletop player is
        // forced into. Deterministic auto-pick per D-24192; no prompt.
        if (playerZones.hand.length < omenCount) {
            const woundOutcome = gainWoundToDiscard(gameState, playerId);
            pushLog(gameState, woundOutcome !== 'no-wound'
                ? `[Doctor Doom Master Strike] Player ${playerId} could not discard ${omenCount} card(s) (${omenCount} Omen(s)) and ${describeStrikeWound(woundOutcome)}.`
                : `[Doctor Doom Master Strike] Player ${playerId} could not discard ${omenCount} card(s) and the Wound supply is empty — no effect.`);
            continue;
        }
        let discardedCount = 0;
        while (discardedCount < omenCount) {
            const targetExtId = selectLowestCostHero(gameState, playerZones.hand, 'any', null);
            // why: the hand is large enough by count but may hold fewer Heroes than
            // Omens (Wounds are never selectable). Stop rather than loop forever or
            // throw — moves never throw, and the shortfall is logged below.
            if (targetExtId === null) {
                break;
            }
            discardCardFromHand(gameState, playerId, targetExtId);
            discardedCount += 1;
        }
        pushLog(gameState, discardedCount === omenCount
            ? `[Doctor Doom Master Strike] Player ${playerId} discarded ${discardedCount} card(s) for ${omenCount} Omen(s).`
            : `[Doctor Doom Master Strike] Player ${playerId} discarded only ${discardedCount} of ${omenCount} card(s) — no eligible Heroes remained.`);
    }
}
/**
 * Resolves core Loki's Master Strike: "Each player reveals a [hc:strength]
 * Hero or gains a Wound."
 *
 * Each player (current player first, then clockwise; D-24780) who holds a Strength Hero reveals it and
 * KEEPS it — reveal is the printed escape, not a cost. A player holding none
 * gains a Wound (or takes a logged no-op when the Wound supply is empty).
 *
 * Auto-resolve: revealing is never a meaningful choice, so this parks no
 * pending choice. Distinct from co2e Loki (resolveLokiStrike), which prints a
 * different penalty (discard / Hypno-Thrall).
 *
 * Mutates G directly, mirroring resolveCo2eMagnetoStrike. One durable log line
 * per player.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function resolveCoreLokiStrike(gameState, currentPlayer) {
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        const revealedHero = selectLowestCostHero(gameState, playerZones.hand, 'heroClass', HERO_CLASS_STRENGTH);
        // why: reveal is the printed ESCAPE — a player holding a Strength Hero
        // reveals it and KEEPS it, taking no Wound (contrast co2e Loki, which
        // discards). selectLowestCostHero is used only as the existence check here;
        // the revealed card is never removed from hand.
        if (revealedHero !== null) {
            pushLog(gameState, `[Loki Master Strike] Player ${playerId} revealed ${formatCardRef(gameState.cardDisplayData, revealedHero)} — no Wound.`);
            // why: WP-649 / D-24461 — announce the avoided core Loki strike (the fourth
            // and last reveal-and-keep Master Strike producer, deferred by WP-644),
            // additive to the silent reveal-skip. One event per blocking player; the
            // terminal mastermindStrikeResolved still fires below. The WP-644/645 push idiom.
            gameState.notableEvents.push({
                type: 'strikeBlocked',
                playerId,
                threatKind: 'masterStrike',
                narrative: composeStrikeBlockedNarrative('masterStrike'),
            });
            continue;
        }
        const woundOutcome = gainWoundToDiscard(gameState, playerId);
        pushLog(gameState, woundOutcome !== 'no-wound'
            ? `[Loki Master Strike] Player ${playerId} has no Strength Hero in hand and ${describeStrikeWound(woundOutcome)}.`
            : `[Loki Master Strike] Player ${playerId} has no Strength Hero in hand and the Wound supply is empty — no effect.`);
    }
}
/**
 * Resolves Baron Heinrich Zemo's Master Strike (WP-818 / D-24752): "Each player
 * KOs a Bystander from their Victory Pile. Any player who cannot do so gains a
 * Wound."
 *
 * Each player (current player first, then clockwise; D-24780) KOs the first Bystander in their Victory
 * Pile to the KO pile; a player with none gains a Wound (or takes a logged
 * no-op when the Wound supply is empty). One durable log line per player.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function resolveBaronZemoStrike(gameState, currentPlayer) {
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        // why: every Victory-Pile Bystander is worth the same 1 VP with its rescue ability
        // spent, so the first in pile order loses nothing (the D-24749 Executioner precedent).
        const bystander = findFirstVictoryPileBystander(playerZones.victory);
        if (bystander !== undefined) {
            const moved = moveCardFromZone(playerZones.victory, gameState.ko, bystander);
            playerZones.victory = moved.from;
            gameState.ko = moved.to;
            pushLog(gameState, `[Baron Zemo Master Strike] Player ${playerId} KO'd a Bystander from their Victory Pile.`);
            continue;
        }
        const woundOutcome = gainWoundToDiscard(gameState, playerId);
        if (woundOutcome !== 'no-wound') {
            pushLog(gameState, `[Baron Zemo Master Strike] Player ${playerId} had no Bystander in their Victory Pile and ${describeStrikeWound(woundOutcome)}.`);
        }
        else {
            pushLog(gameState, `[Baron Zemo Master Strike] Player ${playerId} had no Bystander in their Victory Pile and the Wound supply is empty — no effect.`);
        }
    }
}
/**
 * Resolves Arnim Zola's Master Strike (WP-821 / D-24754): "For each Hero in the HQ that
 * has less than 2 printed [icon:attack], put that Hero on the bottom of the Hero Deck, and
 * each player discards a card of that Hero's cost."
 *
 * In HQ slot order, each qualifying Hero goes to the bottom of the Hero Deck, its slot
 * refills from the top, and each player discards a hand card of that Hero's cost. Never
 * throws.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function resolveArnimZolaStrike(gameState, currentPlayer) {
    // why: "for each Hero in the HQ" names the Heroes there when the strike resolves, so the
    // slots are snapshotted first. A Hero that refills a vacated slot is never re-checked,
    // even when weak; iterating the live HQ would chain refills through the Hero Deck.
    const weakSlots = listZolaWeakHqSlots(gameState);
    if (weakSlots.length === 0) {
        pushLog(gameState, `[Arnim Zola Master Strike] No Hero in the HQ has less than 2 printed Attack — no effect.`);
        return;
    }
    for (const slot of weakSlots) {
        const heroId = gameState.hq[slot];
        if (heroId === null || heroId === undefined) {
            continue;
        }
        // why: the discard cost is the moved Hero's own off-play cost, read before the refill.
        const cost = offPlayCardStats(gameState, heroId)?.cost ?? 0;
        // why: the bottom of the Hero Deck is the array end (heroDeck[0] is the top); push then
        // refill, so the strike never changes the Hero Deck's length, and with an empty deck the
        // same Hero returns to its slot (the resolveOptionalPutBottomHQ behavior).
        gameState.heroDeck.push(heroId);
        const refillResult = refillHqSlot(gameState.hq, slot, gameState.heroDeck);
        gameState.hq = refillResult.hq;
        gameState.heroDeck = refillResult.heroDeck;
        pushLog(gameState, `[Arnim Zola Master Strike] ${formatCardRef(gameState.cardDisplayData, heroId)} has less than 2 printed Attack and went to the bottom of the Hero Deck.`);
        discardCardOfCostForEachPlayer(gameState, cost, currentPlayer);
    }
}
/**
 * Makes each player discard one hand card of the given cost, if they hold one, for
 * Arnim Zola's Master Strike.
 *
 * // why: WP-821 / D-24754 — a Master Strike has no `events`, so it cannot park a
 * multi-seat choice inline, and every candidate shares the same cost. The engine
 * auto-picks inline at strike timing: the earliest such card in hand order, the card the
 * WP-817 seat-choice default picks. An interactive pick is out of scope.
 *
 * @param gameState - The game state to mutate.
 * @param cost - The removed Hero's cost.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function discardCardOfCostForEachPlayer(gameState, cost, currentPlayer) {
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const cardId = selectDefaultCostDiscardCard(gameState, playerId, [cost]);
        if (cardId === null) {
            pushLog(gameState, `[Arnim Zola Master Strike] Player ${playerId} has no card that costs ${cost} to discard.`);
            continue;
        }
        discardCardFromHand(gameState, playerId, cardId);
        pushLog(gameState, `[Arnim Zola Master Strike] Player ${playerId} discarded ${resolveCardName(gameState.cardDisplayData, cardId)} (cost ${cost}).`);
    }
}
/**
 * Resolves co2e Loki's Master Strike: "Each player discards a
 * [hc:strength] Hero or stacks a non-grey Hero from their hand next to Loki
 * as a Hypno-Thrall."
 *
 * Each player (current player first, then clockwise; D-24780): a player holding a Strength Hero discards
 * their lowest-cost one (the printed first cost). A player with no Strength
 * Hero but at least one non-grey Hero has their lowest-cost non-grey Hero
 * stacked next to Loki as a Hypno-Thrall (WP-398, closing the WP-388 gap). A
 * player with neither keeps a logged no-op — no Wound is substituted and
 * nothing is stacked.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function resolveLokiStrike(gameState, currentPlayer) {
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        const strengthHeroExtId = selectLowestCostHero(gameState, playerZones.hand, 'heroClass', HERO_CLASS_STRENGTH);
        // why: WP-388 — the printed FIRST cost is "discards a [hc:strength] Hero".
        // A player holding one discards their lowest-cost Strength Hero. This branch
        // is byte-identical to WP-388's (AC-1).
        if (strengthHeroExtId !== null) {
            discardCardFromHand(gameState, playerId, strengthHeroExtId);
            pushLog(gameState, `[Loki Master Strike] Player ${playerId} discarded ${formatCardRef(gameState.cardDisplayData, strengthHeroExtId)}.`);
            continue;
        }
        // why: WP-398 / D-24192 — the printed SECOND branch, closing the gap WP-388
        // left open: "or stacks a non-grey Hero from their hand next to Loki as a
        // Hypno-Thrall." Discard-first is the tabletop "or" resolution — the second
        // branch fires ONLY when the first cannot (the player holds no Strength
        // Hero), because discarding a card you chose beats surrendering a Hero
        // permanently (D-24188 deterministic auto-pick; there is no meaningful
        // player choice to park).
        const thrallExtId = selectLowestCostHero(gameState, playerZones.hand, 'non-grey', null);
        if (thrallExtId !== null) {
            // why: stacking a Thrall is NOT a discard — the card is removed from hand
            // and stacked next to Loki — so it uses moveCardFromZone (the WP-382 /
            // D-24183 idiom, with BOTH returned arrays assigned back) rather than the
            // discardFromHand chokepoint, which would wrongly fire the WP-498
            // return-on-discard reaction.
            //
            // why: `?? []` guards a legacy or undefined zone — hypnoThralls is
            // optional on the G type so existing fixtures compile; production setup
            // always seeds it `[]`. The handler never throws (AC-7).
            const currentThralls = gameState.mastermind.hypnoThralls ?? [];
            const moveResult = moveCardFromZone(playerZones.hand, currentThralls, thrallExtId);
            playerZones.hand = moveResult.from;
            gameState.mastermind.hypnoThralls = moveResult.to;
            pushLog(gameState, `[Loki Master Strike] Player ${playerId} stacked ${formatCardRef(gameState.cardDisplayData, thrallExtId)} next to Loki as a Hypno-Thrall.`);
            continue;
        }
        // why: WP-398 — a player with NEITHER a Strength Hero NOR any non-grey Hero
        // (only grey cards or Wounds) keeps the logged no-op. No Wound is
        // substituted and nothing is stacked — the faithful "or" resolution for a
        // player who can pay neither cost (AC-3). This replaces WP-388's escape,
        // which fired for any player lacking a Strength Hero.
        pushLog(gameState, `[Loki Master Strike] Player ${playerId} has no Strength Hero and no non-grey Hero in hand — no effect.`);
    }
}
/**
 * Resolves co2e Magneto's Master Strike: "Each player discards an
 * [team:x-men] Hero or gains a Wound."
 *
 * Each player (current player first, then clockwise; D-24780) discards their lowest-cost X-Men Hero, or
 * gains a Wound when they hold none.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function resolveCo2eMagnetoStrike(gameState, currentPlayer) {
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        const targetExtId = selectLowestCostHero(gameState, playerZones.hand, 'team', TEAM_X_MEN);
        // why: this branch is fully faithful — a player holding no X-Men Hero
        // must take the Wound at the table too, so no fidelity is lost.
        if (targetExtId === null) {
            const woundOutcome = gainWoundToDiscard(gameState, playerId);
            pushLog(gameState, woundOutcome !== 'no-wound'
                ? `[Magneto Master Strike] Player ${playerId} has no X-Men Hero in hand and ${describeStrikeWound(woundOutcome)}.`
                : `[Magneto Master Strike] Player ${playerId} has no X-Men Hero in hand and the Wound supply is empty — no effect.`);
            continue;
        }
        discardCardFromHand(gameState, playerId, targetExtId);
        pushLog(gameState, `[Magneto Master Strike] Player ${playerId} discarded ${formatCardRef(gameState.cardDisplayData, targetExtId)}.`);
    }
}
/**
 * Resolves co2e Doctor Octopus's Master Strike: "Each player may discard a
 * [team:spider-friends] Hero. Any player who doesn't must reveal the top 8
 * cards of their deck, discard all non-grey Heroes revealed, and put the rest
 * back in random order."
 *
 * Each player (current player first, then clockwise; D-24780) discards their lowest-cost Spider-Friends
 * Hero.
 *
 * @param gameState - The game state to mutate.
 * @param currentPlayer - The active player (null in stub contexts → sorted order); the
 *   each-player loop starts with them (D-24780).
 */
function resolveDoctorOctopusStrike(gameState, shuffleFunction, currentPlayer) {
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780); a null
    // currentPlayer (stub contexts) keeps the sorted order. The reveal branch reshuffles in
    // this order too, so the RNG stream follows it.
    const playerIds = seatsInTurnOrderFrom(Object.keys(gameState.playerZones), currentPlayer);
    for (const playerId of playerIds) {
        const playerZones = gameState.playerZones[playerId];
        const targetExtId = selectLowestCostHero(gameState, playerZones.hand, 'team', TEAM_SPIDER_FRIENDS);
        // why: taking the discard branch is the player-optimal read of the printed
        // "may" — surrendering one chosen Hero beats revealing eight cards and
        // losing every non-grey Hero among them. D-24192 auto-pick.
        if (targetExtId !== null) {
            discardCardFromHand(gameState, playerId, targetExtId);
            pushLog(gameState, `[Doctor Octopus Master Strike] Player ${playerId} discarded ${formatCardRef(gameState.cardDisplayData, targetExtId)}.`);
            continue;
        }
        // why: WP-397 / D-24200 — the printed alternative for a player who does
        // not discard: "reveal the top 8 cards of their deck, discard all non-grey
        // Heroes revealed, and put the rest back in random order."
        resolveDoctorOctopusReveal(gameState, playerId, playerZones, shuffleFunction);
    }
}
/**
 * Resolves one player's reveal-eight branch of Doctor Octopus's Master Strike.
 *
 * @param gameState - The game state to mutate (log lines).
 * @param playerId - The player being resolved, for log lines.
 * @param playerZones - The player's zones, mutated in place.
 * @param shuffleFunction - Deterministic shuffle, or null when unavailable.
 */
function resolveDoctorOctopusReveal(gameState, playerId, 
// why: WP-482 / D-24288 — widened from the former inline { deck, discard } shape to
// the full PlayerZones so reshuffleDiscardIntoDeck (which requires PlayerZones) type-
// checks. The runtime object is already a PlayerZones (the caller passes
// gameState.playerZones[playerId]!); this is a pure type change, no runtime effect.
playerZones, shuffleFunction) {
    // why: WP-482 / D-24288 — "reveal the top 8 … put the rest back in random order" is a
    // reveal, so a short deck reshuffles the discard to reveal a full 8 — the standard
    // Legendary reveal-reshuffle rule (superseding D-24200's "revealed as-is, never topped
    // up" clause + the D-24285 reveal-eight carve-out; both blockers are moot — the printed
    // text is "random order" so NO player reorder is needed, and WP-478 shipped the helper).
    // The top-up makes the strike HARSHER (reveals more → discards more non-grey Heroes), the
    // faithful reading — NOT a benefit (the random shuffle-back below still denies deck info).
    // Skip when the discard is empty or no shuffle source is available (reveal as-is, the
    // former D-24200 path). shuffleFunction wrapped as a ShuffleProvider for the pure helper.
    if (playerZones.deck.length < DOCTOR_OCTOPUS_REVEAL_COUNT &&
        playerZones.discard.length > 0 &&
        shuffleFunction !== null) {
        reshuffleDiscardIntoDeck(playerZones, { random: { Shuffle: shuffleFunction } });
    }
    const revealCount = Math.min(DOCTOR_OCTOPUS_REVEAL_COUNT, playerZones.deck.length);
    if (revealCount === 0) {
        pushLog(gameState, `[Doctor Octopus Master Strike] Player ${playerId} has an empty deck — nothing revealed.`);
        return;
    }
    // why: index 0 is the top of the deck — the engine's draw convention
    // (`drawCardsIntoHand` reads `deck[0]`).
    const revealed = playerZones.deck.slice(0, revealCount);
    const untouchedTail = playerZones.deck.slice(revealCount);
    const discardedCards = [];
    const returnedCards = [];
    for (const cardExtId of revealed) {
        // why: D-24783 — the revealed cards are off play (the deck), so the shared grey predicate
        // reads a split card as both faces' classes; a Wound or Bystander is not a Hero and stays.
        if (matchesGreyHeroFilter(gameState, cardExtId, 'non-grey')) {
            discardedCards.push(cardExtId);
        }
        else {
            returnedCards.push(cardExtId);
        }
    }
    // why: the remainder goes back in RANDOM order because the printed effect
    // exists to deny the player free deck information — returning eight cards in
    // a known order would turn the strike into a benefit. ctx.random.Shuffle is
    // the framework PRNG and the only permitted randomness, so replay stays
    // faithful. A context without Shuffle (unit-test stubs pass `{}`) degrades
    // to revealed order, which is deterministic and logged rather than silent.
    const returnedInOrder = shuffleFunction
        ? shuffleFunction(returnedCards)
        : returnedCards;
    playerZones.deck = [...returnedInOrder, ...untouchedTail];
    playerZones.discard = [...playerZones.discard, ...discardedCards];
    const orderNote = shuffleFunction ? '' : ' (revealed order — no shuffle available)';
    pushLog(gameState, `[Doctor Octopus Master Strike] Player ${playerId} revealed ${revealCount} card(s), discarded ${discardedCards.length} non-grey Hero(es), and returned ${returnedInOrder.length} to the top of their deck${orderNote}.`);
}
/**
 * Resolves General "Thunderbolt" Ross's Master Strike — the flip (WP-669 / D-24483).
 *
 * The printed text is "General Ross [keyword:Transforms], then [Cross-Dimensional Hulk
 * Rampage]" on the General Ross face and "Red Hulk [keyword:Transforms], then …" on the
 * Red Hulk face. This flips the boss to its other face via transformMastermind (which
 * swaps baseCardId ↔ alternateFaceId and updates gameText); the new face's fightCost is
 * already in G.cardStats and its display in G.cardDisplayData, so fightMastermind and the
 * UIState projection follow with no extra work.
 *
 * The named ride-along effect ([keyword:Cross-Dimensional Hulk Rampage] / [keyword:Wounded
 * Fury]) is HONEST-PARTIAL this slice — logged as unmodeled, not resolved.
 *
 * If the mastermind was not captured with a second face (not in the setup allowlist),
 * `alternateFaceId` is absent and the flip is a no-op — logged as an honest hollow so the
 * unmodeled transform is observable rather than silent. Never throws.
 *
 * @param gameState - The game state; `gameState.mastermind` is rebound to the flipped face.
 */
function resolveGeneralRossStrike(gameState) {
    const mastermind = gameState.mastermind;
    if (mastermind.alternateFaceId === undefined) {
        // why: reached only if General Ross is not in the MASTERMIND_TRANSFORM allowlist, so
        // its second face was dropped at setup and it cannot flip. Log a `blocked` hollow so
        // the unmodeled transform is visible rather than a silent no-op.
        pushLog(gameState, `[Master Strike] ${resolveCardName(gameState.cardDisplayData, mastermind.baseCardId)} would transform, but its second boss face was not loaded — the transform is unmodeled.`, 'blocked');
        return;
    }
    const fromName = resolveCardName(gameState.cardDisplayData, mastermind.baseCardId);
    // why: the flip returns a new MastermindState (copy-then-override); rebind it so every
    // unrelated field (tactics, strikePile, attachedBystanders) survives.
    gameState.mastermind = transformMastermind(mastermind);
    const toName = resolveCardName(gameState.cardDisplayData, gameState.mastermind.baseCardId);
    // why: WP-434 — a realized flip is `applied` (green): the boss swapped faces.
    pushLog(gameState, `[Master Strike] ${fromName} transformed into ${toName}.`, 'applied');
    // why: WP-669 honest-partial — the named ride-along effect ([Cross-Dimensional Hulk
    // Rampage] / [Wounded Fury]) is not modeled this slice; log it `neutral` so the gap is
    // observable without claiming the whole strike is blocked.
    pushLog(gameState, `[Master Strike] ${toName}'s follow-on effect is not yet modeled.`, 'neutral');
}
/**
 * Mastermind strike handler dispatcher.
 *
 * Branches on `G.selection.mastermindId`. Per-mastermind text effects mutate
 * G directly. A Master Strike resolves only the Mastermind's printed text, so
 * it never captures a Bystander on its own (D-24654). The returned
 * RuleEffect[] carries only the shared counter increment and message —
 * card-specific work is done inline.
 *
 * @param gameState - Current game state (mutated by the per-mastermind effects).
 * @param strikeContext - The rule-pipeline context; read for the active player
 *   (interactive strike choices) and for the deterministic shuffle (Doctor Octopus).
 * @param payload - Trigger payload `{ cardId }` from villain reveal.
 *   WP-200: read to source `strikeCardId` for the terminal
 *   `mastermindStrikeResolved` emission. Production dispatch always
 *   passes a real `{ cardId: string }`; unit tests that call the handler
 *   directly with a stub fall back to an empty string so the emission
 *   produces a well-typed event without throwing.
 * @param _implementationMap - Handler map (unused; reserved for future cascading strikes).
 * @returns Array of RuleEffect descriptions to apply.
 */
export function mastermindStrikeHandler(gameState, strikeContext, payload, _implementationMap) {
    // why: rules v23 ~L3429 — "When a Master Strike occurs, each Mastermind does its
    // Master Strike ability", so each Mastermind resolves only its own printed strike.
    // D-24654 removed the D-15401 generic capture-a-Bystander placeholder.
    // why: branches are mutually exclusive per mastermind id — a mastermind
    // matching none of them takes no branch and the strike is generic
    // bookkeeping only (its printed text is not yet implemented).
    const mastermindId = gameState.selection.mastermindId;
    // why: WP-846 / D-24780 — each each-player strike receives the active player
    // (resolveCurrentPlayer; null in stub contexts → sorted order) so its loop starts with the
    // current player and goes clockwise (rules v23 L2247).
    if (mastermindId === MASTERMIND_MAGNETO) {
        // why: WP-476 / D-24284 — pass the active player so the strike can park an
        // interactive discard choice for the current player (narrowed from the
        // RevealContext, mirroring resolveShuffleFunction for Doctor Octopus).
        resolveMagnetoStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (MASTERMINDS_RED_SKULL.includes(mastermindId)) {
        // why: WP-577 / D-24386 — pass the active player so the strike can park an
        // interactive hand-scoped KO choice for the current player (mirroring the
        // resolveMagnetoStrike current-parks / allies-auto pattern, D-24284).
        resolveRedSkullStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === MASTERMIND_CORE_LOKI) {
        resolveCoreLokiStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === MASTERMIND_CORE_DR_DOOM) {
        // why: WP-538 / D-24347 — pass the active player so the strike can park an
        // interactive put-cards-on-deck choice for the current player (mirroring
        // the resolveMagnetoStrike interactive branch).
        resolveCoreDoomStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === MASTERMIND_CO2E_DOCTOR_DOOM) {
        resolveDoctorDoomStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === MASTERMIND_CO2E_LOKI) {
        resolveLokiStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === MASTERMIND_CO2E_MAGNETO) {
        resolveCo2eMagnetoStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === MASTERMIND_CO2E_DOCTOR_OCTOPUS) {
        resolveDoctorOctopusStrike(gameState, resolveShuffleFunction(strikeContext), resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === MASTERMIND_GENERAL_ROSS) {
        // why: WP-669 / D-24483 — General Ross's Master Strike flips the Mastermind
        // to its other boss face (General Ross ⇄ Red Hulk).
        resolveGeneralRossStrike(gameState);
    }
    else if (mastermindId === BARON_ZEMO_MASTERMIND_ID) {
        // why: WP-818 / D-24752 — "Each player KOs a Bystander from their Victory Pile. Any
        // player who cannot do so gains a Wound."
        resolveBaronZemoStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    else if (mastermindId === ARNIM_ZOLA_MASTERMIND_ID) {
        // why: WP-821 / D-24754 — "For each Hero in the HQ that has less than 2 printed
        // [icon:attack], put that Hero on the bottom of the Hero Deck, and each player discards
        // a card of that Hero's cost."
        resolveArnimZolaStrike(gameState, resolveCurrentPlayer(strikeContext));
    }
    // why: WP-200 — terminal emission AFTER the per-mastermind text effect
    // (there is no generic capture to wait for since D-24654). Narrows the trigger
    // payload defensively (`unknown` from the rule pipeline) so a missing
    // / malformed payload produces an empty `strikeCardId` rather than a
    // throw — moves never throw, per architecture rules. Production dispatch
    // path from `villainDeck.reveal.ts` always supplies `{ cardId }` so the
    // empty-string fallback only surfaces in unit-test harnesses that
    // pre-date WP-200.
    const strikeCardId = (() => {
        if (payload === null || payload === undefined)
            return '';
        if (typeof payload !== 'object')
            return '';
        const candidate = payload.cardId;
        if (typeof candidate !== 'string')
            return '';
        return candidate;
    })();
    // why: WP-200 — defensive access (`?.`) because some legacy test states
    // do not populate `cardDisplayData`. Production setup always builds it
    // (WP-111), but tests that pre-date that field cast through `as unknown
    // as LegendaryGameState` and leave it undefined. Falling back to the
    // raw `strikeCardId` keeps the emission from throwing — moves never
    // throw, per architecture rules.
    const strikeCardDisplay = gameState.cardDisplayData?.[strikeCardId];
    const strikeCardName = strikeCardDisplay && typeof strikeCardDisplay.name === 'string' && strikeCardDisplay.name.length > 0
        ? strikeCardDisplay.name
        : strikeCardId;
    gameState.notableEvents.push({
        type: 'mastermindStrikeResolved',
        strikeCardId,
        narrative: composeMastermindStrikeNarrative(strikeCardName),
    });
    return buildGenericStrikeEffects();
}
//# sourceMappingURL=mastermindHandlers.js.map